import { StrictMode } from 'react';
import { LazyMotion } from 'framer-motion';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx';
import './index.css';
import i18n, { i18nReady } from './i18n';
import { DarkModeProvider, CurrencyProvider } from './context';
import { afterPageSettles } from './lib/deferred';

// Register the service worker only after the page settles: its install step
// precaches the whole site, which shouldn't compete with the first load.
afterPageSettles(() => {
  void import('virtual:pwa-register').then(({ registerSW }) => {
    const updateSW = registerSW({
      // `load` has already fired by now; without this it would never register.
      immediate: true,
      onNeedRefresh() {
        if (confirm(i18n.t('pwa.newContent', 'New content available. Reload?'))) {
          void updateSW(true);
        }
      },
      onOfflineReady() {
        console.log('App ready to work offline');
      },
    });
  });
});

const loadMotionFeatures = () => import('./lib/motionFeatures').then((mod) => mod.default);

// Render once the page's language is loaded (instant for English, one small
// chunk otherwise) — the boot skeleton covers the page until then. A failed
// chunk load still renders, falling back to English.
const render = () =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <LazyMotion features={loadMotionFeatures}>
        <HelmetProvider>
          <BrowserRouter>
            <DarkModeProvider>
              <CurrencyProvider>
                <App />
              </CurrencyProvider>
            </DarkModeProvider>
          </BrowserRouter>
        </HelmetProvider>
      </LazyMotion>
    </StrictMode>
  );

i18nReady.then(render, render);
